export const BRAND_TOKENS = [
  { name: 'white', hex: '#FFFFFF' },
  { name: 'dark blue', hex: '#010A41' },
  { name: 'red', hex: '#F10D59' },
  { name: 'blue', hex: '#0040EE' },
  { name: 'pink', hex: '#FF3EDE' },
  { name: 'purple', hex: '#5A22A9' },
  { name: 'light blue', hex: '#00D4CC' },
  { name: 'orange', hex: '#FF6D2A' },
  { name: 'yellow', hex: '#FCFA38' },
  { name: 'green', hex: '#72E54C' },
  { name: 'black', hex: '#000000' },
]

export const colorGLSL = `
uniform vec2 u_colorAngleRot;  // (cos(colorAngle), sin(colorAngle)) precomputed CPU-side
uniform vec3 u_colorStop0;
uniform vec3 u_colorStop1;
uniform vec3 u_colorStop2;
uniform float u_colorPos0;
uniform float u_colorPos1;
uniform float u_colorPos2;

// Webapp-identity overlay. Mirrors the CSS rule
//   linear-gradient(180deg, secondary 20%, secondary@alpha=0.1 100%)
// so the host no longer needs a separate DOM layer that re-composites on scroll.
uniform vec3 u_secondaryColor;

vec3 sampleColorGradient(vec2 uv) {
  float t = clamp(dot(uv - 0.5, u_colorAngleRot) + 0.5, 0.0, 1.0);

  vec3 c;
  if (t <= u_colorPos0) {
    c = u_colorStop0;
  } else if (t <= u_colorPos1) {
    c = mix(u_colorStop0, u_colorStop1, (t - u_colorPos0) / (u_colorPos1 - u_colorPos0));
  } else if (t <= u_colorPos2) {
    c = mix(u_colorStop1, u_colorStop2, (t - u_colorPos1) / (u_colorPos2 - u_colorPos1));
  } else {
    c = u_colorStop2;
  }
  return c;
}

vec3 overlayBlend(vec3 base, vec3 blend) {
  return mix(
    2.0 * base * blend,
    1.0 - 2.0 * (1.0 - base) * (1.0 - blend),
    step(0.5, base)
  );
}

// CSS 0% is at the top of the canvas; gl_FragCoord.y increases upward in WebGL,
// so uv.y=1 is the top. cssPos = 1 - uv.y therefore runs 0 at top → 1 at bottom,
// matching the CSS gradient parameter space. Alpha is 1.0 above the 20% stop
// and lerps to 0.1 across the remaining 80%.
vec3 applyWebappOverlay(vec3 color, vec2 uv) {
  float cssPos = 1.0 - uv.y;
  float t = clamp((cssPos - 0.2) / 0.8, 0.0, 1.0);
  float alpha = mix(1.0, 0.1, t);
  return mix(color, u_secondaryColor, alpha);
}
`

export const colorDefaults = {
  colorStopCount: 2,
  colorAngle: 235.0,
  colorStop0: [0.004, 0.039, 0.255], // dark blue #010A41
  colorStop1: [1.0, 0.245, 0.871], // pink #FF3EDE
  colorStop2: [0.353, 0.133, 0.663], // purple (inactive when count=2)
  colorPos0: 0.0,
  colorPos1: 1.0,
  colorPos2: 0.5,
  secondaryColor: [0.004, 0.039, 0.255], // dark blue #010A41 (webapp default)
}

export function createColorUniforms(gl, program) {
  return {
    u_colorAngleRot: gl.getUniformLocation(program, 'u_colorAngleRot'),
    u_colorStop0: gl.getUniformLocation(program, 'u_colorStop0'),
    u_colorStop1: gl.getUniformLocation(program, 'u_colorStop1'),
    u_colorStop2: gl.getUniformLocation(program, 'u_colorStop2'),
    u_colorPos0: gl.getUniformLocation(program, 'u_colorPos0'),
    u_colorPos1: gl.getUniformLocation(program, 'u_colorPos1'),
    u_colorPos2: gl.getUniformLocation(program, 'u_colorPos2'),
    u_secondaryColor: gl.getUniformLocation(program, 'u_secondaryColor'),
  }
}

export function setColorUniforms(gl, uniforms, state) {
  const rad = (state.colorAngle * Math.PI) / 180
  gl.uniform2f(uniforms.u_colorAngleRot, Math.cos(rad), Math.sin(rad))

  // collect active stops and sort by position (matches Figma behaviour)
  const stops = [
    { color: state.colorStop0, pos: state.colorPos0 },
    { color: state.colorStop1, pos: state.colorPos1 },
  ]
  if (state.colorStopCount >= 3) {
    stops.push({ color: state.colorStop2, pos: state.colorPos2 })
  }
  stops.sort((a, b) => a.pos - b.pos)

  // pad to 3 by duplicating last stop
  while (stops.length < 3) {
    stops.push({
      color: stops[stops.length - 1].color,
      pos: stops[stops.length - 1].pos,
    })
  }

  gl.uniform3fv(uniforms.u_colorStop0, stops[0].color)
  gl.uniform3fv(uniforms.u_colorStop1, stops[1].color)
  gl.uniform3fv(uniforms.u_colorStop2, stops[2].color)
  gl.uniform1f(uniforms.u_colorPos0, stops[0].pos)
  gl.uniform1f(uniforms.u_colorPos1, stops[1].pos)
  gl.uniform1f(uniforms.u_colorPos2, stops[2].pos)
  gl.uniform3fv(uniforms.u_secondaryColor, state.secondaryColor)
}

// Helpers for hex ↔ RGB array conversion
export function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  return [r, g, b]
}

export function rgbToHex(rgb) {
  const r = Math.round(rgb[0] * 255)
    .toString(16)
    .padStart(2, '0')
  const g = Math.round(rgb[1] * 255)
    .toString(16)
    .padStart(2, '0')
  const b = Math.round(rgb[2] * 255)
    .toString(16)
    .padStart(2, '0')
  return '#' + r + g + b
}
